// ─────────────────────────────────────────────────────────────────────────────
//  LabUnpaidSettings — "Unpaid Lab & Scan Order Visibility"
//
//  Separate from the pharmacy equivalent on purpose: the two rooms sit at
//  different points in the payment queue, and a facility can reasonably run the
//  dispensary open and the laboratory closed, or the reverse.
//
//  Wording is written for a LAB. A laboratory does not "dispense" — it runs a
//  test and enters a result, so "show it but do not dispense" would read as
//  nonsense to a technician.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack, Radio, RadioGroup, FormControlLabel, FormControl, Typography,
  Button, CircularProgress, Alert, Chip, Box,
} from '@mui/material';
import {
  SaveOutlined as SaveIcon,
  VisibilityOffOutlined as HideIcon,
  VisibilityOutlined as ViewIcon,
  ScienceOutlined as WorkIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import { labUnpaidVisibility } from '../BaseURL';

const MODES = [
  {
    value: 'hide',
    icon: <HideIcon fontSize="small" />,
    title: 'Hide unpaid orders',
    sees: 'The laboratory sees nothing until the cash desk records payment.',
    may:  'No result can be entered before payment.',
    isDefault: true,
  },
  {
    value: 'view',
    icon: <ViewIcon fontSize="small" />,
    title: 'Show unpaid orders, but do not allow results',
    sees: 'The order appears marked UNPAID, so the lab can see what was requested, prepare the bench and flag a reagent that has run out.',
    may:  'Entering a result stays blocked until payment is recorded.',
  },
  {
    value: 'serve',
    icon: <WorkIcon fontSize="small" />,
    title: 'Show unpaid orders and allow results',
    sees: 'The order appears marked UNPAID.',
    may:  'The test can be run and its result entered before payment — for staff, credit patients and emergencies.',
  },
];

const LabUnpaidSettings = () => {
  const [mode, setMode]       = useState('hide');
  const [saved, setSaved]     = useState('hide');
  const [migrated, setMigrated] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(labUnpaidVisibility);
      const v = res.data?.unpaidVisibility;
      const next = ['hide', 'view', 'serve'].includes(v) ? v : 'hide';
      setMode(next);
      setSaved(next);
      setMigrated(res.data?.migrationApplied !== false);
    } catch (err) {
      // Fail closed in the UI too, so nobody reads this screen as "open"
      // when the setting could not actually be read.
      setMode('hide');
      setSaved('hide');
      setError(
        err.response?.status === 404
          ? 'This server does not have the setting yet — deploy the latest backend.'
          : (err.response?.data?.message || err.message || 'Could not load the setting.')
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (mode === saved) return;

    if (mode === 'serve') {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Allow results before payment?',
        html: `
          <div style="text-align:left;font-size:0.9rem">
            <p>The laboratory will be able to run tests and enter results for orders
               that have not been paid for.</p>
            <p style="color:#b45309">Only choose this if your facility settles afterwards
               — staff, credit patients or emergencies.</p>
          </div>`,
        showCancelButton: true,
        confirmButtonColor: '#b45309',
        cancelButtonColor: '#9e9e9e',
        confirmButtonText: 'Yes, allow it',
      });
      if (!confirm.isConfirmed) return;
    }

    setSaving(true);
    try {
      const res = await axios.put(labUnpaidVisibility, { unpaidVisibility: mode });
      if (res.data?.success) {
        setSaved(res.data.unpaidVisibility);
        setMode(res.data.unpaidVisibility);
        setMigrated(res.data.migrationApplied !== false);
        await Swal.fire({
          icon: 'success', title: 'Saved',
          text: 'The laboratory and scan rooms will pick this up within 30 seconds.',
          timer: 2200, showConfirmButton: false,
        });
      } else {
        throw new Error(res.data?.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        icon: 'error', title: 'Save Failed',
        text: err.response?.data?.message || err.message,
        confirmButtonColor: '#c0392b',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="warning" sx={{ fontSize: '0.82rem' }}>{error}</Alert>}

      {!migrated && (
        <Alert severity="info" sx={{ fontSize: '0.82rem' }}>
          This database has not had migration 032 applied yet, so no order is
          recorded before payment and this setting has nothing to act on. The
          laboratory behaves exactly as it does today until the migration runs.
        </Alert>
      )}

      <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>
        Applies to laboratory tests and scans. Free and insurance-covered orders
        were never hidden — they have always appeared as soon as the clinician
        requested them — so this setting only changes what happens to a
        <strong> costed</strong> order before the patient reaches the cash desk.
      </Typography>

      <FormControl>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODES.map(m => (
            <Box
              key={m.value}
              sx={{
                border: '1px solid',
                borderColor: mode === m.value ? '#11998e' : '#e2e8f0',
                bgcolor: mode === m.value ? 'rgba(17,153,142,0.04)' : '#fff',
                borderRadius: 2, p: 1.5, mb: 1, transition: 'all .15s',
              }}
            >
              <FormControlLabel
                value={m.value}
                control={<Radio size="small" sx={{ color: '#11998e', '&.Mui-checked': { color: '#11998e' } }} />}
                sx={{ alignItems: 'flex-start', m: 0 }}
                label={
                  <Box sx={{ pt: 0.25 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                      {m.icon}
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
                        {m.title}
                      </Typography>
                      {m.isDefault && (
                        <Chip label="Default" size="small"
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                bgcolor: 'rgba(100,116,139,0.12)', color: '#475569' }} />
                      )}
                      {saved === m.value && (
                        <Chip label="Active" size="small"
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                bgcolor: 'rgba(16,185,129,0.14)', color: '#15803D' }} />
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: '0.76rem', color: '#475569' }}>
                      <strong>Sees:</strong> {m.sees}
                    </Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: '#475569' }}>
                      <strong>May do:</strong> {m.may}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          ))}
        </RadioGroup>
      </FormControl>

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained" onClick={handleSave}
          disabled={saving || mode === saved}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
          sx={{ textTransform: 'none', fontWeight: 700, background: '#11998e',
                '&:hover': { background: '#0e8a7f' } }}
        >
          {saving ? 'Saving…' : mode === saved ? 'Saved' : 'Save change'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default LabUnpaidSettings;
